import enNavigation from './en/navigation';
import enEaiSubjects from './en/eai/subjects';
import enEaiUsers from './en/eai/users';
import enEaiDeliveryPoints from './en/eai/deliveryPoints';
import enEaiBalanceGroups from './en/eai/balanceGroups';
import enEaiDevices from './en/eai/devices';
import enEaiDeliveryContracts from 'assets/translations/en/eai/deliveryContracts';
import enEaiCodebooks from './en/eai/codebooks';
import enEaiMessages from './en/eai/messages';
import enDispatcherProductionPlanning from 'assets/translations/en/dispatcher/productionPlanning';
import enCalendar from 'assets/translations/en/dispatcher/calendar';
import enDispatcherCodebooks from 'assets/translations/en/dispatcher/codebooks';
import enDispatcherOnlineDispatching from 'assets/translations/en/dispatcher/onlineDispatching';
import { i18nTranslation } from 'models/i18n';
import {
  DeviceInstallationStatus,
  EMSMemberAccountStatus,
  MarketPeriodsType,
  UserAccountStatus
} from 'utils/constants';
import { DispatcherValidationError } from 'models/dispatcher';
import enDispatcherWeatherWidget from 'assets/translations/en/dispatcher/weatherWidget';
import { caReasonsCodesEN } from 'assets/translations/en/caReasonsCodes/caReasonsCodes';
import reporting from './en/reporting/reporting';
import enHomePage from 'assets/translations/en/homePage';
import enSystemCodebooks from 'assets/translations/en/system/codebooks';
import enSystemActivityLogs from 'assets/translations/en/system/activityLogs';
import enSystemActivityEvents from 'assets/translations/en/system/activityEvents';
import enHxDashboard from 'assets/translations/en/dispatcher/hxDashboard';
import enRkDashboard from 'assets/translations/en/dispatcher/rkDashboard';
import { GridDensity } from 'components/dataGrid/dataGridModels';
import { AlarmsColumn } from 'views/modules/System/Alarms/alarmsModels';
import { ProductionModelProfileType } from 'components/productionModelConfiguration/productionModelUtils';

// ==============================|| I18N EN ||============================== //

const translate: i18nTranslation = {
  header: {
    localization: {
      sk: 'Slovensky',
      en: 'English'
    },
    profile: {
      darkMode: 'Dark Mode',
      logout: 'Sign out',
      changeSubject: 'Change subject',
      version: 'App version'
    }
  },
  navigation: enNavigation,
  module: {
    crossModule: {
      productionModel: {
        listOfModels: {
          actions: {
            addModel: 'Add model',
            editModel: 'Edit model',
            removeModel: 'Remove model',
            removeAllModels: 'Remove all models',
            showModel: 'Show model'
          },
          columns: {
            startDate: 'Start date',
            endDate: 'End date',
            profileType: 'Profile type'
          },
          errors: {
            overlappingDateRange: 'Date range overlaps with another model',
            validationErrorInModel: 'Model contains validation errors',
            rangeError: 'End date must be after start date'
          },
          isDefault: 'Default'
        },
        modelDetail: {
          actions: {
            startDate: 'Start date',
            endDate: 'End date',
            profileType: 'Profile type'
          },
          columns: {
            period: 'Period',
            commonProductionMwh: 'Energy [MWh]',
            workdaysProductionMwh: 'Workdays energy [MWh]',
            weekendProductionMwh: 'Weekend energy [MWh]'
          },
          isDefault: 'Mark as default'
        },
        profileType: {
          [ProductionModelProfileType.COMMON]: 'Unified profile',
          [ProductionModelProfileType.WORKDAYS_WEEKENDS]:
            'Workdays and weekends'
        }
      }
    },
    dispatcher: {
      onlineDispatching: enDispatcherOnlineDispatching,
      productionPlanning: enDispatcherProductionPlanning,
      operationCalendar: enCalendar,
      hxDashboard: enHxDashboard,
      rkDashboard: enRkDashboard,
      periodText: ' market period',
      currentTime: 'Current time: ',
      dataGrid: {
        columns: {
          title: 'Columns'
        },
        density: {
          title: 'Dense',
          [GridDensity.COMPACT]: 'Compact',
          [GridDensity.STANDARD]: 'Standard',
          [GridDensity.COMFORTABLE]: 'Comfortable'
        }
      },
      messages: {
        eventCreated: 'Event created successfully',
        errorCreatingEvent: 'Error creating event',
        updateOperationCalendarEvent: 'Event updated successfully',
        invalidateOperationEvent: 'Event invalidated successfully',
        deleteOperationCalendarEvent: 'Event deleted successfully',
        productionPlanCreated: 'Production plan successfully created',
        productionPlanUpdated:
          'New revision of production plan successfully created',
        productionPlanPropagated:
          'Revision of production plan successfully propagated',
        productionPlanConfirmed:
          'Revision of production plan successfully confirmed',
        productionPlanCopied:
          'Revision of production plan successfully copied to the selected days',
        dailyDiagramCreated: 'Daily diagram successfully created',
        dailyDiagramUpdated: 'Daily diagram successfully updated',
        dailyDiagramAccepted: 'Daily diagram successfully accepted',
        dailyDiagramValidationErrors:
          'Daily diagram cannot be accepted. Before accepting, following errors must be removed:',
        dailyDiagramValidationWarningsPrefix: 'Daily diagram contains alerts:',
        dailyDiagramValidationWarningsPostfix:
          'Are you sure you want to accept daily diagram despite the warnings?',
        [DispatcherValidationError.MUST_BE_INTEGER]:
          'The value must be an integer',
        [DispatcherValidationError.MAX_2_DECIMALS]:
          'The value must be a number with maximum 2 decimal places',
        [DispatcherValidationError.MAX_3_DECIMALS]:
          'The value must be a number with maximum 3 decimal places',
        [DispatcherValidationError.MAX_5_DECIMALS]:
          'The value must be a number with maximum 5 decimal places',
        [DispatcherValidationError.MAX_6_DECIMALS]:
          'The value must be a number with maximum 6 decimal places',
        [DispatcherValidationError.MIN_VALUE]:
          'The value must not be less than ',
        [DispatcherValidationError.MAX_VALUE]:
          'The value must not be greater than ',
        [DispatcherValidationError.MUST_BE_ONE_OF]:
          'Value must be one of the values: ',
        [DispatcherValidationError.NOT_A_NUMBER]: 'Value must be number',
        [DispatcherValidationError.POSITIVE_NUMBER]:
          'Value must be a positive number',
        [DispatcherValidationError.EXPONENTIAL_NUMBER]:
          'Number must not be in an exponential format'
      },
      codebooks: enDispatcherCodebooks,
      weatherWidget: enDispatcherWeatherWidget
    },
    eai: {
      users: enEaiUsers,
      subjects: enEaiSubjects,
      balanceGroups: enEaiBalanceGroups,
      deliveryPoints: enEaiDeliveryPoints,
      devices: enEaiDevices,
      deliveryContracts: enEaiDeliveryContracts,
      codebooks: enEaiCodebooks,
      messages: enEaiMessages
    },
    reporting,
    homePage: enHomePage,
    system: {
      activityEvents: enSystemActivityEvents,
      codebooks: enSystemCodebooks,
      activityLogs: enSystemActivityLogs,
      alarms: {
        resolveAlarmDialog: {
          title: 'Resolve alarm',
          description:
            'Are you sure you want to resolve this alarm? You need to enter a note to resolve it.',
          inputLabel: 'Note',
          resolveButton: 'Yes resolve'
        },
        cancelAlarmDialog: {
          title: 'Cancel alarm',
          description:
            'Are you sure you want to cancel this alarm? You need to enter a note to cancel it.',
          inputLabel: 'Note',
          cancelButton: 'Yes cancel'
        },
        filter: {
          triggeredFrom: 'Date from',
          triggeredTo: 'Date to',
          buttonApply: 'Apply',
          dateRangeError: 'Enter max week date range'
        },
        table: {
          columns: {
            [AlarmsColumn.TRIGGERED_AT]: 'Triggered at',
            [AlarmsColumn.CODE]: 'Code',
            [AlarmsColumn.SEVERITY]: 'Severity',
            [AlarmsColumn.STATUS]: 'Status',
            [AlarmsColumn.PARAMETERS]: 'Alarm',
            [AlarmsColumn.TAGS]: 'Tags',
            [AlarmsColumn.MARKET_PARTICIPANT]: 'Market participant',
            [AlarmsColumn.BALANCING_GROUP]: 'Balance group',
            [AlarmsColumn.DELIVERY_POINT]: 'Delivery point',
            [AlarmsColumn.DEVICE]: 'Device',
            [AlarmsColumn.ACTIONS]: 'Actions'
          }
        }
      }
    }
  },
  pages: {
    crashFallback: {
      headline: 'Oops, something went wrong',
      message: 'Internal error occurred',
      messageCopied: 'Error message copied to clipboard: ',
      button: {
        copy: 'Copy error message',
        restore: 'Restore'
      }
    }
  },
  dialogs: {
    businessTimeDialog: {
      title: 'Please check your system time settings',
      message:
        'Your system time {{systemTime}} is is off from the actual business time {{businessTime}}'
    },
    selectEmsMemberAccount: {
      title: 'Choose subject to work with',
      noSubjectDescriptionBegin:
        'The user has no associated subject account. For more information, contact the administrator or',
      noSubjectLogoutButton: 'sign in with a different user account'
    },
    addCredentials: {
      title: 'Add credentials',
      addCredentialsButton: 'Add credentials'
    },
    removeCredentials: {
      title: 'Remove credentials',
      description:
        'Are you sure you want to remove credentials? After remove user cannot access external service anymore.',
      submitButtonTitle: 'Yes remove'
    },
    emsMemberAccountStateDialog: {
      description: {
        [EMSMemberAccountStatus.ACTIVE]:
          "Are you sure you want to invalidate the subject's account? Upon revocation, the account will be temporarily locked and the user will not be able to access their data or perform any activities on the platform.",
        [EMSMemberAccountStatus.INACTIVE]:
          "Are you sure you want to recover the subject's account? After recovery, the user will be able to use his account without restrictions."
      },
      submitButtonTitle: {
        [EMSMemberAccountStatus.ACTIVE]: 'Yes invalidate',
        [EMSMemberAccountStatus.INACTIVE]: 'Yes recover'
      }
    },
    userAccountStateDialog: {
      description: {
        [UserAccountStatus.ENABLED]:
          "Are you sure you want to disable the user's account? Upon revocation, the account will be temporarily locked and the user will not be able to access their data or perform any activities on the platform.",
        [UserAccountStatus.DISABLED]:
          "Are you sure you want to enable the subject's account? After recovery, the user will be able to use his account without restrictions."
      },
      submitButtonTitle: {
        [UserAccountStatus.ENABLED]: 'Yes, disable',
        [UserAccountStatus.DISABLED]: 'Yes, enable'
      }
    },
    resetPasswordDialog: {
      description:
        "Are you sure you want to reset password ? A password reset request will be sent to the user's email address ",
      submitButtonTitle: 'Yes, confirm'
    },
    deleteSubjectDialog: {
      description:
        'Are you sure you want to permanently remove the subject from the system? The action cannot be undone',
      submitButtonTitle: 'Yes delete'
    },
    unassignUserDialog: {
      title: 'Unassign user subject account',
      description: 'Are you sure you want to unassign user subject account?',
      submitButtonTitle: 'Yes unassign'
    },
    deleteUserRoleDialog: {
      title: 'Delete user role',
      description: 'Are you sure you want to delete user role?',
      submitButtonTitle: 'Yes delete'
    },
    setDeviceStatusDialog: {
      description: {
        [DeviceInstallationStatus.INSTALLED]:
          'Are you sure you want to set device status as uninstalled?',
        [DeviceInstallationStatus.NOT_INSTALLED]:
          'Are you sure you want to set device status as installed'
      },
      submitButtonTitle: {
        [DeviceInstallationStatus.INSTALLED]: 'Yes, uninstall',
        [DeviceInstallationStatus.NOT_INSTALLED]: 'Yes, install'
      }
    },
    createGDSDialog: {
      title: 'Create GDS',
      description: 'Create GDS for delivery point',
      submitButtonTitle: 'Submit',
      backButtonTitle: 'Back'
    },
    changeDPAssignmentDialog: {
      title: 'Change assignment',
      description: 'Change delivery point assignment',
      submitButtonTitle: 'Change assignment',
      backButtonTitle: 'Back'
    },
    deleteDevice: {
      title: 'Remove device',
      description: 'Are you sure you want to remove device?',
      submitButtonTitle: 'Yes remove'
    },
    deleteDeviceGroup: {
      title: 'Remove device group',
      description: 'Are you sure you want to remove device group?',
      submitButtonTitle: 'Yes remove'
    },
    unassignDeliveryPoint: {
      title: 'Unassign delivery point',
      description: 'Are you sure you want to unassign delivery point?',
      submitButtonTitle: 'Yes unassign'
    },
    deleteUserDialog: {
      title: 'Delete user',
      description:
        'Are you sure you want to permanently delete user from the system? This action cannot be undone.',
      submitButtonTitle: 'Yes delete'
    },
    invalidateGDS: {
      title: 'Invalidate GDS',
      description:
        'Are you sure you want to invalidate the selected gateway? The action cannot be undone.',
      submitButtonTitle: 'Invalidate'
    }
  },
  common: {
    noItems: 'No items',
    unitsOfMeasure: {
      kWh: 'kWh',
      kW: 'kW',
      MWh: 'MWh',
      kVAr: 'kVAr',
      PERCENT: '%',
      EUR: '€',
      A: 'A',
      V: 'V',
      METERS_ABOVE_SEA_LEVEL: 'MSL',
      CUBIC_METRE_PER_SECOND: 'm³/s',
      Hz: 'Hz',
      EUR_PER_MEGAWATT_HOUR: '€/MWh',
      EUR_PER_KILOWATT_HOUR: '€/kWh',
      DEGREE_CELSIA: '°C',
      MILLIMETRE: 'mm',
      METRE_PER_SECOND: 'm/s',
      Pa: 'Pa',
      kW_PER_SQUARE_METRE: 'kW/m²',
      W_PER_SQUARE_METRE: 'W/m²'
    },
    marketPeriod: {
      [MarketPeriodsType.MARKET_PERIODS]: 'Market periods',
      [MarketPeriodsType.MARKET_HOURS]: 'Hours'
    },
    wizard: {
      review: 'Review'
    },
    table: {
      filter: 'Filter',
      validFor: 'Validity',
      actions: 'Actions',
      current: 'Current',
      inputSearch: 'Search',
      rowsPerPage: 'Rows per page:',
      of: 'of',
      moreThan: 'more than',
      clearFilter: 'Clear all',
      selectAll: 'Select all',
      detail: 'Detail',
      delete: 'Delete',
      edit: 'Edit',
      resultsNotFound: 'No results found',
      unassign: 'Unassign GDS',
      unknownType: 'Unknown Type',
      loading: 'Loading ...'
    },
    button: {
      back: 'Back',
      next: 'Next',
      cancel: 'Cancel',
      saveChanges: 'Save changes',
      add: 'Add',
      invalidate: 'Invalidate',
      ok: 'OK',
      propagate: 'Propagate',
      apply: 'Apply',
      forceAccept: 'Accept despite warnings',
      edit: 'Edit',
      export: 'Export',
      clearSelection: 'Clear selection',
      understand: 'I understand',
      login: 'Log in'
    },
    status: {
      ACTIVE: 'Active',
      INACTIVE: 'Inactive',
      BLOCKED: 'Blocked'
    },
    messages: {
      caReasons: caReasonsCodesEN,
      ifNotMatch:
        'The information has changed since the page was last loaded and is no longer current.',
      ifNotMatchThen: 'Do you wish to',
      forceSave: 'save your changes',
      or: 'or',
      discardChanges: 'update information',
      somethingIsWrong: 'Something is wrong',
      pageNotFound:
        'The page you are looking was moved, removed, renamed, or might never exist :(',
      noMenuPermissionsLabel: 'You have no permissions assigned',
      noMenuPermissionsText:
        'You have no permissions for current subject. Please, contact administrator for adding permissions.',
      noAccessPermissionsForPageLabel: 'Access denied',
      noAccessPermissionsForPageText:
        'You have no permissions for current page. Please, contact administrator for adding permissions.',
      noPagePermissionsForPageLabel: 'Action denied',
      noPagePermissionsForPageText:
        'You are not authorized to perform the given action. Please, contact administrator for adding permissions.',
      sessionTimeoutDialogLabel: 'You have been signed out of system',
      sessionTimeoutDialogText:
        'Your session has expired. Please sign in again.'
    },
    chart: {
      noData: 'No data found',
      period: 'period'
    },
    httpErrorStatusMessages: {
      error400BadRequest:
        'An unexpected error occurred in the request (contact your system administrator)',
      error401Unauthorized:
        'You are not authorized to perform this operation (contact your system administrator)',
      error403Forbidden:
        'The action cannot be performed at the moment (repeat the action later or contact the system administrator)',
      error404NotFound:
        'The entity does not exist in the system or has been removed',
      error500InternalError:
        'An unexpected error occurred (contact your system administrator)'
    },
    qualityFlags: {
      qualityFlag: 'Quality flag',
      qualityFlagCodes: {
        OK: 'OK',
        A: 'Approximate value',
        SB: 'Substituted value',
        T: 'Test value',
        M: 'Manually entered value',
        OV: 'Overflowed value',
        NT: 'Non topical value',
        OB: 'Operator blocked signal',
        IV: 'Invalid value'
      }
    }
  }
};

export default translate;
